import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { IMAGES } from '../../constants/images';

const testimonials = [
  {
    quote: "TOF Consultancy brought clarity and precision to our subsidy application process. Their in-depth knowledge of government schemes made a significant difference for our hospitality project.",
    name: "Vivek Choudhary",
    designation: "Director",
    company: "Holiday Inn Express Jaipur",
    image: IMAGES.vivek
  },
  {
    quote: "TOF Consultancy's expertise in financial solutions has greatly benefited our operations at Akshat Apartments Pvt. Ltd. Their guidance has been invaluable in navigating complex financial landscapes.",
    name: "Ved Prakash Sujaka",
    designation: "DGM - Finance",
    company: "Akshat Apartments Private Limited",
    image: IMAGES.ved
  },
  {
    quote: "TOF Consultancy's financial guidance was instrumental in navigating our growth as an NSE listed medical devices company and exporter. Their expertise is unmatched.",
    name: "Uday Chandra Jha",
    designation: "Senior Manager",
    company: "Poly Medicure Limited",
    image: IMAGES.uday
  },
  {
    quote: "We would like to thank team TOF for the work done for the organization. They are very clear what is required and made sure work was completed at the fastest pace in the shortest time.",
    name: "Sukhet Gupta",
    designation: "Vice President",
    company: "Shri Krishna Rolling Mills Limited",
    image: IMAGES.sukhet
  },
  {
    quote: "The team at TOF demonstrated exceptional professionalism in handling our financial requirements. Their guidance on RIPS subsidies helped us optimize our expansion plans.",
    name: "Mohit Soni",
    designation: "Director",
    company: "Soni Medicare Ltd",
    image: IMAGES.mohit
  },
  {
    quote: "Working with TOF has been a game-changer for our manufacturing unit. Their expertise in navigating government incentive schemes is truly commendable.",
    name: "Chetan M Mokzaria",
    designation: "Managing Director",
    company: "Minova Runaya Private Limited",
    image: IMAGES.chetan
  },
  {
    quote: "TOF's comprehensive approach to subsidy consultation helped us secure significant government benefits. Their team's dedication and expertise are outstanding.",
    name: "Basant Kumar Jain",
    designation: "Director",
    company: "Shri Adinath Cashew Pvt Ltd",
    image: IMAGES.basant
  },
  {
    quote: "The professional guidance from TOF Consultancy made our subsidy application process smooth and successful. Highly recommended for any business seeking government benefits.",
    name: "Aayush Kogta",
    designation: "Director",
    company: "Kogta Housing Development",
    image: IMAGES.aayush
  }
];

export const HomeTestimonials = () => {
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const testimonialScrollRef = useRef(null);

  const scrollToTestimonial = (index) => {
    if (testimonialScrollRef.current) {
      const cardWidth = 360;
      testimonialScrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setActiveTestimonialIndex(index);
    }
  };

  const handleTestimonialScroll = () => {
    if (testimonialScrollRef.current) {
      const scrollLeft = testimonialScrollRef.current.scrollLeft;
      const cardWidth = 360;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveTestimonialIndex(newIndex);
    }
  };

  useEffect(() => {
    const scrollElement = testimonialScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleTestimonialScroll);
      return () => scrollElement.removeEventListener('scroll', handleTestimonialScroll);
    }
  }, []);

  return (
    <section className="py-24 bg-[#f8fafc]" data-testid="home-testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white rounded-full text-sm font-medium text-[#3b5998] mb-4 shadow-sm">
            Client Testimonials
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-4">
            What Our Clients Say
          </h2>
          <p className="text-[#64748b] max-w-2xl mx-auto">
            Real feedback from real clients who have experienced our services.
          </p>
        </motion.div>

        <div className="relative">
          <button
            onClick={() => scrollToTestimonial(Math.max(0, activeTestimonialIndex - 1))}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-[#3b5998] hover:bg-[#3b5998] hover:text-white transition-colors hidden md:flex"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => scrollToTestimonial(Math.min(testimonials.length - 1, activeTestimonialIndex + 1))}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-[#3b5998] hover:bg-[#3b5998] hover:text-white transition-colors hidden md:flex"
            aria-label="Next testimonial"
          >
            <ChevronRight size={20} />
          </button>

          <div
            ref={testimonialScrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-6 px-4 md:px-12 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex-shrink-0 w-[320px] md:w-[360px] snap-center"
              >
                <Card className="h-full bg-white border border-slate-100 hover:border-[#00bcd4]/30 hover:shadow-lg transition-all overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-[#00bcd4]"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                          {testimonial.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-[#0f172a]">{testimonial.name}</p>
                        <p className="text-[#64748b] text-sm">{testimonial.designation}</p>
                        <p className="text-[#00bcd4] text-sm font-medium">{testimonial.company}</p>
                      </div>
                    </div>

                    <div className="relative">
                      <Quote className="w-8 h-8 text-[#00bcd4]/20 absolute -top-2 -left-2" />
                      <blockquote className="text-[#64748b] leading-relaxed text-sm pl-6 line-clamp-4">
                        "{testimonial.quote}"
                      </blockquote>
                    </div>

                    <div className="flex gap-1 mt-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#00bcd4] text-[#00bcd4]" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeTestimonialIndex ? 'bg-[#3b5998] w-6' : 'bg-slate-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/clients"
            className="inline-flex items-center gap-2 text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
          >
            View All Testimonials
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};
